/* eslint-disable react/require-default-props */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
   FaChevronRight,
   FaUser,
   FaLink,
   FaRegCalendarAlt,
   FaRegClock,
   FaReceipt,
   FaSlidersH,
   FaWrench,
   FaUsers,
   FaRegStickyNote,
   FaCheck,
   FaExclamationTriangle,
} from 'react-icons/fa';

import {
   createScheduleItem,
   listServiceCatalog,
} from '../../../../services/routing/routingApi';
import {
   getCustomer,
   listAddresses,
} from '../../../../services/customers/customersApi';
import { getDepartments } from '../../../../services/hr/hrApi';
import EmployeeNameSearch from '../../../../components/EmployeeNameSearch';
import CurrencyInput from '../../../../components/MinorUtilities/CurrencyInput';
import { SmallButton } from '../../../../styles/ui/Button';
import ServiceSelector from '../../_shared/ServiceSelector';
import RestoreItemDialog from '../../RoutingItemShow/components/RestoreItemDialog';
import {
   indexCatalogById,
   suggestedDurationMinutes,
} from '../../RouteManagement/serviceFields';
import AddressFormModal from '../../../Comercial/ClienteShow/components/AddressFormModal';

import CustomerLinkedBlock from './manual/CustomerLinkedBlock';
import NewCustomerModal from './manual/NewCustomerModal';
import ConfirmItemModal from './manual/ConfirmItemModal';
import {
   formatAddressLine,
   extractNumericValueFromCurrencyString,
} from './manual/manualHelpers';
import {
   FormCard,
   FormCardTop,
   Section,
   SectionHead,
   SectionIcon,
   SectionTitles,
   FormGrid,
   Field,
   FieldLabel,
   FieldInput,
   FieldSelect,
   FieldTextarea,
   FieldError,
   ControlScope,
   WindowBlock,
   Collapse,
   CollapseHead,
   CollapseBody,
   Subhead,
   Subdesc,
   PermNote,
   InlineCheckboxLabel,
   FormFoot,
   GhostButton,
   PrimaryButton,
} from './manual/styles';

// ===========================================================================
// Constantes
// ===========================================================================
const SCHEDULE_PERIOD_MORNING = 'MANHA';
const SCHEDULE_PERIOD_AFTERNOON = 'TARDE';

// EmployeeNameSearch — params estáveis (module-level evita loop de re-render)
const SELLER_SEARCH_PARAMS = {
   is_salesperson: 1,
   employment_status: 'ATIVO',
};

// Tipos de cobrança (enum real payment_collection_type — handoff 0051 §2.2)
const PAYMENT_COLLECTION_TYPES = [
   { value: 'PENDENTE', label: 'Pendente' },
   { value: 'ANTECIP_OU_NO_ATO', label: 'Antecipado ou no ato' },
   { value: 'A_PRAZO', label: 'A prazo' },
   { value: 'ISENTO', label: 'Isento' },
   { value: 'OUTROS', label: 'Outros' },
];

// Limites do schema (handoff 0051 §2.2)
const MAX_LENGTH_ORDER_NUMBER = 20;

// ===========================================================================
// Helpers
// ===========================================================================
function buildInitialFormData(initialScheduleDate, initialSchedulePeriod) {
   return {
      // Agendamento
      schedule_date: initialScheduleDate,
      schedule_period: initialSchedulePeriod,
      customer_window_start: '',
      customer_window_end: '',

      // Pedido
      order_number: '',
      order_value_formatted: '', // string do CurrencyInput; convertida no submit

      // Atribuições — departamento (só enviado se canAllocate). Técnico NÃO é
      // atribuído no cadastro: é definido depois, na gestão de rotas
      // (designate/assign). Atribuir aqui violaria a camada de agendamento.
      department_id: '',

      // Vendedor (sempre disponível)
      seller_employee_id: null,
      is_salesperson_external: false,

      // Execução
      payment_collection_type: '',
      services: [], // [{ commission_table_id, quantity }]
      notes: '',
   };
}

function formatDurationLabel(min) {
   if (!min) return '0 min';
   const hours = Math.floor(min / 60);
   const minutes = min % 60;
   if (hours && minutes) return `${hours}h${String(minutes).padStart(2, '0')}`;
   if (hours) return `${hours}h`;
   return `${minutes} min`;
}

// ===========================================================================
// Componente
// ===========================================================================
export default function ManualEntryTab({
   scheduleDate,
   schedulePeriod,
   onScheduleDateChange,
   onSchedulePeriodChange,
   clusterOrigin = 'GOIANIA',
   clusterOrigins = [],
   onClusterOriginChange,
   canAllocate = false,
}) {
   const navigate = useNavigate();

   // Reagendamento a partir do board (Parte D): a nova guia abre com
   // ?order_number=... para não obrigar a redigitar a chave de rastreio. Seed
   // único no mount; o resto o operador preenche (cliente/endereço inclusive).
   const [searchParams] = useSearchParams();

   const baseOptions =
      clusterOrigins.length > 0
         ? clusterOrigins
         : [{ origin_code: clusterOrigin, origin_label: clusterOrigin }];

   // ---------------------- State ----------------------
   const [formData, setFormData] = useState(() => {
      const initial = buildInitialFormData(scheduleDate, schedulePeriod);
      const seededOrderNumber = searchParams.get('order_number');
      if (seededOrderNumber) {
         initial.order_number = seededOrderNumber.slice(
            0,
            MAX_LENGTH_ORDER_NUMBER,
         );
      }
      return initial;
   });
   const formDataRef = useRef(formData);
   useEffect(() => {
      formDataRef.current = formData;
   }, [formData]);

   // Cliente / endereço — sempre vinculado a um cliente cadastrado (o botão
   // "Novo cliente" cria um quando não existe). Não há mais modo avulso inline:
   // endereço atomizado vive em customer_addresses (ADR-009).
   const [customerSearchText, setCustomerSearchText] = useState('');
   const [selectedCustomer, setSelectedCustomer] = useState(null);
   const [addresses, setAddresses] = useState([]);
   const [selectedAddressId, setSelectedAddressId] = useState(null);
   const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
   const customerRequestRef = useRef(0);

   const [departments, setDepartments] = useState([]);
   const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
   const [serviceCatalog, setServiceCatalog] = useState([]);

   const [sellerSearchText, setSellerSearchText] = useState('');

   const [isOptionalDetailsExpanded, setIsOptionalDetailsExpanded] =
      useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [fieldErrors, setFieldErrors] = useState({});

   const [activeModal, setActiveModal] = useState(null); // 'address' | 'customer'
   const [isConfirmOpen, setIsConfirmOpen] = useState(false);

   // Duplicata CANCELADA (handoff 0059 §3.1): quando o 409 identifica que a
   // chave está ocupada por um item cancelado (code duplicate_cancelled_item +
   // existing_item_id), guardamos o alvo pro atalho "Restaurar item #id" que
   // aparece junto do erro do campo de pedido — { id, order_number } | null.
   const [restorableDuplicate, setRestorableDuplicate] = useState(null);
   const [isRestoreOpen, setIsRestoreOpen] = useState(false);

   // ---------------------- Sincroniza data/período do orquestrador ----------------------
   useEffect(() => {
      setFormData((prev) => ({
         ...prev,
         schedule_date: scheduleDate,
         schedule_period: schedulePeriod,
      }));
   }, [scheduleDate, schedulePeriod]);

   // ---------------------- Carrega departamentos no mount ----------------------
   useEffect(() => {
      let isMounted = true;
      (async () => {
         setIsLoadingDepartments(true);
         try {
            const result = await getDepartments();
            if (isMounted) setDepartments(result || []);
         } catch (err) {
            if (isMounted) {
               toast.error(
                  err?.response?.data?.message ??
                     'Erro ao carregar departamentos.',
               );
            }
         } finally {
            if (isMounted) setIsLoadingDepartments(false);
         }
      })();
      return () => {
         isMounted = false;
      };
   }, []);

   // ---------------------- Carrega o catálogo de serviços no mount ----------------------
   useEffect(() => {
      let isMounted = true;
      listServiceCatalog()
         .then((list) => {
            if (isMounted) setServiceCatalog(Array.isArray(list) ? list : []);
         })
         .catch(() => {
            if (isMounted) setServiceCatalog([]);
         });
      return () => {
         isMounted = false;
      };
   }, []);

   // ---------------------- Helpers de campo ----------------------
   const updateFieldError = useCallback((field, message) => {
      setFieldErrors((prev) => {
         if (!message) {
            const next = { ...prev };
            delete next[field];
            return next;
         }
         return { ...prev, [field]: message };
      });
   }, []);

   const updateField = useCallback(
      (field, value) => {
         setFormData((prev) => ({ ...prev, [field]: value }));
         updateFieldError(field, null);
      },
      [updateFieldError],
   );

   const handleScheduleDateChange = useCallback(
      (newValue) => {
         onScheduleDateChange?.(newValue);
         updateField('schedule_date', newValue);
      },
      [onScheduleDateChange, updateField],
   );

   const handleSchedulePeriodChange = useCallback(
      (newValue) => {
         onSchedulePeriodChange?.(newValue);
         updateField('schedule_period', newValue);
      },
      [onSchedulePeriodChange, updateField],
   );

   const handleSellerSelect = useCallback(
      (employee) => {
         if (employee) {
            updateField('seller_employee_id', employee.id);
            setSellerSearchText(employee.full_name ?? '');
         } else {
            updateField('seller_employee_id', null);
            setSellerSearchText('');
         }
      },
      [updateField],
   );

   // ---------------------- Cliente / endereço (Modo A) ----------------------
   // Enriquece o cliente escolhido: getCustomer traz telefone/refiltek (header
   // do preview); listAddresses é o endpoint canônico da lista de endereços
   // (handoff 0051 §3.2) — mesma forma usada no recarregamento pós-criação.
   const enrichAndSelectCustomer = useCallback(async (customerLite) => {
      const requestId = customerRequestRef.current + 1;
      customerRequestRef.current = requestId;

      setSelectedCustomer(customerLite);
      setAddresses([]);
      setSelectedAddressId(null);
      setIsLoadingAddresses(true);
      setFieldErrors((prev) => {
         const next = { ...prev };
         delete next.customer_address_id;
         return next;
      });

      try {
         const [full, addrs] = await Promise.all([
            getCustomer(customerLite.id),
            listAddresses(customerLite.id),
         ]);
         if (customerRequestRef.current !== requestId) return;
         setSelectedCustomer(full ?? customerLite);
         const list = Array.isArray(addrs) ? addrs : [];
         setAddresses(list);
         const primary = list.find((a) => a.is_primary) || list[0] || null;
         setSelectedAddressId(primary ? primary.id : null);
      } catch (err) {
         if (customerRequestRef.current !== requestId) return;
         toast.error(
            err?.response?.data?.message ??
               'Não foi possível carregar os dados do cliente.',
         );
      } finally {
         if (customerRequestRef.current === requestId) {
            setIsLoadingAddresses(false);
         }
      }
   }, []);

   const handleClearCustomer = useCallback(() => {
      customerRequestRef.current += 1;
      setSelectedCustomer(null);
      setAddresses([]);
      setSelectedAddressId(null);
      setCustomerSearchText('');
      updateFieldError('customer_address_id', null);
   }, [updateFieldError]);

   const handleSelectAddress = useCallback(
      (addressId) => {
         setSelectedAddressId(addressId);
         updateFieldError('customer_address_id', null);
      },
      [updateFieldError],
   );

   // Recarrega a lista após criar um endereço, selecionando o recém-criado.
   const reloadAddressesSelectingNew = useCallback(async (customerId, prevIds) => {
      setIsLoadingAddresses(true);
      try {
         const addrs = await listAddresses(customerId);
         setAddresses(addrs);
         const created = addrs.find((a) => !prevIds.has(a.id));
         const pick =
            created || addrs.find((a) => a.is_primary) || addrs[0] || null;
         if (pick) {
            setSelectedAddressId(pick.id);
            updateFieldError('customer_address_id', null);
         }
      } catch (err) {
         toast.error(
            err?.response?.data?.message ?? 'Erro ao recarregar endereços.',
         );
      } finally {
         setIsLoadingAddresses(false);
      }
   }, [updateFieldError]);

   // ---------------------- Catálogo/duração derivada ----------------------
   const catalogById = useMemo(
      () => indexCatalogById(serviceCatalog),
      [serviceCatalog],
   );
   const durationMinutes = useMemo(
      () => suggestedDurationMinutes(formData.services, catalogById),
      [formData.services, catalogById],
   );

   // ---------------------- Validação ----------------------
   const validate = useCallback(() => {
      const data = formDataRef.current;
      const errors = {};

      if (!data.schedule_date) errors.schedule_date = 'Informe a data agendada.';
      if (!data.schedule_period) errors.schedule_period = 'Selecione o período.';
      if (!data.order_number?.trim()) {
         errors.order_number = 'Informe o número do pedido.';
      }

      // Cliente sempre vinculado: exige cliente + endereço escolhidos.
      if (!selectedCustomer) {
         errors.customer_address_id = 'Selecione um cliente.';
      } else if (!selectedAddressId) {
         errors.customer_address_id = 'Escolha um endereço do cliente.';
      }

      // Janela de horário marcado — par + ordem (backend revalida).
      const ws = data.customer_window_start;
      const we = data.customer_window_end;
      if ((ws && !we) || (!ws && we)) {
         errors.customer_window_end =
            'Preencha início e fim, ou deixe ambos vazios.';
      } else if (ws && we && we <= ws) {
         errors.customer_window_end = 'O fim deve ser maior que o início.';
      }

      return errors;
   }, [selectedCustomer, selectedAddressId]);

   // ---------------------- Payload ----------------------
   const buildPayload = useCallback(() => {
      const data = formDataRef.current;
      const payload = {
         schedule_date: data.schedule_date,
         schedule_period: data.schedule_period,
         order_number: data.order_number.trim(),
      };

      // Cliente sempre vinculado: o backend deriva nome/telefone/endereço/
      // coordenada/refiltek do customer_address (ADR-009).
      payload.customer_address_id = Number(selectedAddressId);

      // Janela — só se ambos preenchidos.
      if (data.customer_window_start && data.customer_window_end) {
         payload.customer_window_start = data.customer_window_start;
         payload.customer_window_end = data.customer_window_end;
      }

      // Base operacional (Ponto 4). Ausente → backend aplica GOIANIA.
      if (clusterOrigin) payload.cluster_origin = clusterOrigin;

      // Valor do pedido
      const orderValueNumeric = extractNumericValueFromCurrencyString(
         data.order_value_formatted,
      );
      if (orderValueNumeric !== null) payload.order_value = orderValueNumeric;

      if (data.payment_collection_type) {
         payload.payment_collection_type = data.payment_collection_type;
      }
      if (data.notes?.trim()) payload.notes = data.notes.trim();

      // Vendedor — sempre disponível.
      if (data.seller_employee_id) {
         payload.seller_employee_id = Number(data.seller_employee_id);
      }
      if (data.is_salesperson_external) payload.is_salesperson_external = 1;

      // Departamento — campo de atribuição gated por canAllocate (o backend
      // exige routing:schedule:manage). Técnico NÃO é enviado: atribuição é da
      // camada de roteirização (board), não do cadastro.
      if (canAllocate && data.department_id) {
         payload.department_id = Number(data.department_id);
      }

      // Seleção de serviços — replace-set. NUNCA enviamos
      // estimated_duration_minutes (derivado pelo backend de services[]).
      payload.services = (data.services || []).map((s) => ({
         commission_table_id: s.commission_table_id,
         quantity: s.quantity,
      }));

      return payload;
   }, [selectedAddressId, clusterOrigin, canAllocate]);

   // ---------------------- Submit (abre confirmação) ----------------------
   const handleRequestConfirm = useCallback(
      (event) => {
         if (event) event.preventDefault();
         const errors = validate();
         setFieldErrors(errors);
         if (Object.keys(errors).length > 0) {
            toast.error('Há campos obrigatórios pendentes.');
            return;
         }
         setIsConfirmOpen(true);
      },
      [validate],
   );

   // ---------------------- Confirma → POST ----------------------
   const handleConfirmSubmit = useCallback(async () => {
      setIsSubmitting(true);
      setRestorableDuplicate(null);
      try {
         const payload = buildPayload();
         const result = await createScheduleItem(payload);
         const createdItem = result.scheduleItem;
         const estimatedDuration = createdItem?.estimated_duration_minutes;
         const successMessage = result.message ?? 'Item criado com sucesso.';
         toast.success(
            estimatedDuration != null
               ? `${successMessage} · duração estimada ${estimatedDuration} min`
               : successMessage,
         );

         if (createdItem?.id) {
            navigate(`/routing/items/${createdItem.id}`);
         } else {
            const params = new URLSearchParams({
               date_from: formDataRef.current.schedule_date,
               date_to: formDataRef.current.schedule_date,
               schedule_period: formDataRef.current.schedule_period,
            });
            navigate(`/routing/items?${params.toString()}`);
         }
      } catch (err) {
         setIsConfirmOpen(false);
         const responseErrors = err?.response?.data?.errors;

         if (Array.isArray(responseErrors) && responseErrors.length > 0) {
            const byField = {};
            let expandOptional = false;
            let reloadAddrs = false;

            responseErrors.forEach((entry) => {
               if (entry?.field) byField[entry.field] = entry.message;

               switch (entry?.code) {
                  case 'invalid_reference':
                     byField.customer_address_id =
                        'Endereço de cliente não encontrado. Recarregue a lista.';
                     reloadAddrs = true;
                     break;
                  case 'duration_without_service':
                     // Backend referencia estimated_duration_minutes (sem input);
                     // remapeia para o bloco de serviços.
                     byField.services =
                        entry.message ??
                        'Adicione ao menos um serviço para estimar a duração.';
                     expandOptional = true;
                     break;
                  case 'allocation_forbidden':
                     expandOptional = true;
                     break;
                  default:
                     break;
               }
            });

            setFieldErrors(byField);
            if (expandOptional) setIsOptionalDetailsExpanded(true);
            if (reloadAddrs && selectedCustomer) {
               reloadAddressesSelectingNew(
                  selectedCustomer.id,
                  new Set(addresses.map((a) => a.id)),
               );
            }

            // 409 com a chave ocupada por item CANCELADO (handoff 0059 §3.1):
            // além do erro inline no order_number (byField, acima), oferecemos
            // o atalho de restauração — restaurar É o desfecho pedido (criar de
            // novo continuaria colidindo).
            const cancelledDuplicate = responseErrors.find(
               (entry) =>
                  entry?.code === 'duplicate_cancelled_item' &&
                  entry?.existing_item_id != null,
            );
            if (cancelledDuplicate) {
               setRestorableDuplicate({
                  id: cancelledDuplicate.existing_item_id,
                  order_number:
                     formDataRef.current.order_number.trim() || null,
               });
            }

            const hasAllocationForbidden = responseErrors.some(
               (entry) => entry?.code === 'allocation_forbidden',
            );
            toast.error(
               hasAllocationForbidden
                  ? 'Você não pode definir o departamento na criação. Remova esse campo.'
                  : err?.response?.data?.message ??
                       'Falha ao cadastrar. Verifique os campos destacados.',
            );
         } else {
            toast.error(err?.response?.data?.message ?? 'Erro ao criar item.');
         }
      } finally {
         setIsSubmitting(false);
      }
   }, [
      buildPayload,
      navigate,
      reloadAddressesSelectingNew,
      selectedCustomer,
      addresses,
   ]);

   // ---------------------- Resumo para o modal de confirmação ----------------------
   const selectedAddress = addresses.find((a) => a.id === selectedAddressId);
   const baseLabel =
      baseOptions.find((o) => o.origin_code === clusterOrigin)?.origin_label ||
      clusterOrigin;

   const confirmSummary = {
      mode: 'LINKED',
      customerName: selectedCustomer?.name || null,
      customerDoc: selectedCustomer?.doc || null,
      addressText: selectedAddress ? formatAddressLine(selectedAddress) : null,
      dateBR: formData.schedule_date
         ? formData.schedule_date.split('-').reverse().join('/')
         : null,
      periodLabel:
         formData.schedule_period === SCHEDULE_PERIOD_MORNING
            ? 'Manhã'
            : 'Tarde',
      baseLabel,
      windowStart: formData.customer_window_start,
      windowEnd: formData.customer_window_end,
      orderNumber: formData.order_number.trim(),
      servicesCount: formData.services.length,
      durationLabel: formatDurationLabel(durationMinutes),
      orderValueFormatted: formData.order_value_formatted.trim(),
   };

   const detailCount =
      formData.services.length +
      (formData.department_id ? 1 : 0) +
      (formData.seller_employee_id ? 1 : 0) +
      (formData.notes.trim() ? 1 : 0);

   // ===========================================================================
   // Render
   // ===========================================================================
   return (
      <form onSubmit={handleRequestConfirm} noValidate>
         <FormCard>
            <FormCardTop>
               <h2>Cadastro manual de item</h2>
               <p>
                  Preencha por contexto. Ao final, o resumo aparece para
                  confirmação antes de cadastrar.
               </p>
            </FormCardTop>

            {/* 1 · Cliente ------------------------------------------------- */}
            <Section>
               <SectionHead>
                  <SectionIcon>
                     <FaUser size={17} />
                     <span className="step">1</span>
                  </SectionIcon>
                  <SectionTitles>
                     <div className="t">Cliente</div>
                     <div className="d">
                        Vincule um cliente do cadastro (ou cadastre um novo)
                     </div>
                  </SectionTitles>
               </SectionHead>

               <CustomerLinkedBlock
                  selectedCustomer={selectedCustomer}
                  addresses={addresses}
                  selectedAddressId={selectedAddressId}
                  isLoadingAddresses={isLoadingAddresses}
                  error={fieldErrors.customer_address_id}
                  searchText={customerSearchText}
                  onSearchTextChange={setCustomerSearchText}
                  onSelectCustomer={enrichAndSelectCustomer}
                  onClearCustomer={handleClearCustomer}
                  onSelectAddress={handleSelectAddress}
                  onOpenNewAddress={() => setActiveModal('address')}
                  onOpenNewCustomer={() => setActiveModal('customer')}
                  disabled={isSubmitting}
               />
            </Section>

            {/* 2 · Agendamento -------------------------------------------- */}
            <Section>
               <SectionHead>
                  <SectionIcon>
                     <FaRegCalendarAlt size={16} />
                     <span className="step">2</span>
                  </SectionIcon>
                  <SectionTitles>
                     <div className="t">Agendamento</div>
                     <div className="d">Quando e de qual base sai a visita</div>
                  </SectionTitles>
               </SectionHead>

               <FormGrid>
                  <Field $span={4}>
                     <FieldLabel htmlFor="schedule_date">
                        Data agendada<span className="req">*</span>
                     </FieldLabel>
                     <FieldInput
                        id="schedule_date"
                        type="date"
                        value={formData.schedule_date}
                        onChange={(e) =>
                           handleScheduleDateChange(e.target.value)
                        }
                        $invalid={!!fieldErrors.schedule_date}
                        disabled={isSubmitting}
                     />
                     {fieldErrors.schedule_date && (
                        <FieldError>
                           <FaExclamationTriangle size={11} />{' '}
                           {fieldErrors.schedule_date}
                        </FieldError>
                     )}
                  </Field>
                  <Field $span={4}>
                     <FieldLabel htmlFor="schedule_period">
                        Período<span className="req">*</span>
                     </FieldLabel>
                     <FieldSelect
                        id="schedule_period"
                        value={formData.schedule_period}
                        onChange={(e) =>
                           handleSchedulePeriodChange(e.target.value)
                        }
                        $invalid={!!fieldErrors.schedule_period}
                        disabled={isSubmitting}
                     >
                        <option value={SCHEDULE_PERIOD_MORNING}>Manhã</option>
                        <option value={SCHEDULE_PERIOD_AFTERNOON}>Tarde</option>
                     </FieldSelect>
                  </Field>
                  <Field $span={4}>
                     <FieldLabel htmlFor="cluster_origin">
                        Base operacional
                     </FieldLabel>
                     <FieldSelect
                        id="cluster_origin"
                        value={clusterOrigin}
                        onChange={(e) => onClusterOriginChange?.(e.target.value)}
                        disabled={isSubmitting}
                     >
                        {baseOptions.map((origin) => (
                           <option
                              key={origin.origin_code}
                              value={origin.origin_code}
                           >
                              {origin.origin_label || origin.origin_code}
                           </option>
                        ))}
                     </FieldSelect>
                  </Field>

                  <WindowBlock $invalid={!!fieldErrors.customer_window_end}>
                     <div className="wl">
                        <FaRegClock size={12} /> Horário marcado (opcional)
                     </div>
                     <div className="win-inputs">
                        <input
                           type="time"
                           value={formData.customer_window_start}
                           onChange={(e) =>
                              updateField(
                                 'customer_window_start',
                                 e.target.value,
                              )
                           }
                           aria-label="Início do horário marcado"
                           disabled={isSubmitting}
                        />
                        <span className="win-sep">até</span>
                        <input
                           type="time"
                           value={formData.customer_window_end}
                           onChange={(e) =>
                              updateField('customer_window_end', e.target.value)
                           }
                           aria-label="Fim do horário marcado"
                           disabled={isSubmitting}
                        />
                     </div>
                     {fieldErrors.customer_window_end ? (
                        <div className="err-msg">
                           <FaExclamationTriangle size={12} />{' '}
                           {fieldErrors.customer_window_end}
                        </div>
                     ) : (
                        <div className="wh">
                           Janela mínima exigida de 60 minutos. Se preenchida, a visita só pode ser roteirizada dentro do horário marcado.
                        </div>
                     )}
                  </WindowBlock>
               </FormGrid>
            </Section>

            {/* 3 · Pedido -------------------------------------------------- */}
            <Section>
               <SectionHead>
                  <SectionIcon>
                     <FaReceipt size={16} />
                     <span className="step">3</span>
                  </SectionIcon>
                  <SectionTitles>
                     <div className="t">Pedido</div>
                     <div className="d">Identificação e valor</div>
                  </SectionTitles>
               </SectionHead>

               <FormGrid>
                  <Field $span={4}>
                     <FieldLabel htmlFor="order_number">
                        Número do pedido<span className="req">*</span>
                     </FieldLabel>
                     <FieldInput
                        id="order_number"
                        type="text"
                        maxLength={MAX_LENGTH_ORDER_NUMBER}
                        value={formData.order_number}
                        onChange={(e) =>
                           updateField('order_number', e.target.value)
                        }
                        placeholder="Ex: 1234567"
                        $invalid={!!fieldErrors.order_number}
                        disabled={isSubmitting}
                     />
                     {fieldErrors.order_number && (
                        <FieldError>
                           <FaExclamationTriangle size={11} />{' '}
                           {fieldErrors.order_number}
                        </FieldError>
                     )}
                     {/* Atalho do 409 duplicate_cancelled_item — some quando o
                         campo é editado (updateField limpa o erro do campo). */}
                     {fieldErrors.order_number && restorableDuplicate && (
                        <div style={{ marginTop: 6 }}>
                           <SmallButton
                              type="button"
                              $intent="write"
                              onClick={() => setIsRestoreOpen(true)}
                           >
                              Restaurar item #{restorableDuplicate.id}
                           </SmallButton>
                        </div>
                     )}
                  </Field>
                  <Field $span={4}>
                     <FieldLabel htmlFor="order_value_formatted">
                        Valor do pedido
                     </FieldLabel>
                     <ControlScope>
                        <CurrencyInput
                           id="order_value_formatted"
                           value={formData.order_value_formatted}
                           onChange={(formattedValue) =>
                              updateField(
                                 'order_value_formatted',
                                 formattedValue,
                              )
                           }
                           disabled={isSubmitting}
                        />
                     </ControlScope>
                  </Field>
                  <Field $span={4}>
                     <FieldLabel htmlFor="payment_collection_type">
                        Tipo de cobrança
                     </FieldLabel>
                     <FieldSelect
                        id="payment_collection_type"
                        value={formData.payment_collection_type}
                        onChange={(e) =>
                           updateField('payment_collection_type', e.target.value)
                        }
                        disabled={isSubmitting}
                     >
                        <option value="">— não definido —</option>
                        {PAYMENT_COLLECTION_TYPES.map((opt) => (
                           <option key={opt.value} value={opt.value}>
                              {opt.label}
                           </option>
                        ))}
                     </FieldSelect>
                  </Field>
               </FormGrid>
            </Section>

            {/* 4 · Detalhes opcionais (colapsável) ------------------------ */}
            <Collapse>
               <CollapseHead
                  type="button"
                  $open={isOptionalDetailsExpanded}
                  onClick={() =>
                     setIsOptionalDetailsExpanded((prev) => !prev)
                  }
               >
                  <span className="chev">
                     <FaChevronRight size={14} />
                  </span>
                  <span className="ic">
                     <FaSlidersH size={16} />
                  </span>
                  <div>
                     <div className="ttl">
                        Serviços, alocação e observações
                     </div>
                     <div className="meta">
                        Opcional — pode ser ajustado depois na tela do item
                     </div>
                  </div>
                  {detailCount > 0 && (
                     <span className="badge">
                        {detailCount} preenchido{detailCount > 1 ? 's' : ''}
                     </span>
                  )}
               </CollapseHead>

               {isOptionalDetailsExpanded && (
                  <CollapseBody>
                     {/* Serviços previstos */}
                     <Subhead $first>
                        <span className="sic">
                           <FaWrench size={14} />
                        </span>
                        <span className="st">Serviços previstos</span>
                     </Subhead>
                     <Subdesc>
                        Serviços do catálogo desta visita. A duração estimada é
                        derivada deles — não há campo manual. Pode ficar vazio e
                        ser preenchido antes de otimizar.
                     </Subdesc>
                     <ServiceSelector
                        value={formData.services}
                        onChange={(next) => updateField('services', next)}
                        catalog={serviceCatalog}
                        disabled={isSubmitting}
                     />
                     {fieldErrors.services && (
                        <FieldError style={{ marginTop: 8 }}>
                           <FaExclamationTriangle size={11} />{' '}
                           {fieldErrors.services}
                        </FieldError>
                     )}

                     {/* Atribuições — departamento + vendedor. O TÉCNICO não é
                         atribuído no cadastro: ele é definido na gestão de rotas
                         (designar/atribuir rota), após a roteirização. */}
                     <Subhead>
                        <span className="sic">
                           <FaUsers size={14} />
                        </span>
                        <span className="st">Atribuições</span>
                     </Subhead>
                     <Subdesc>
                        Departamento e vendedor responsáveis pelo agendamento. O
                        técnico é definido depois, na gestão de rotas — não nesta
                        etapa de cadastro.
                     </Subdesc>
                     <FormGrid>
                        {canAllocate && (
                           <Field $span={6}>
                              <FieldLabel htmlFor="department_id">
                                 Departamento
                              </FieldLabel>
                              <FieldSelect
                                 id="department_id"
                                 value={formData.department_id}
                                 onChange={(e) =>
                                    updateField('department_id', e.target.value)
                                 }
                                 disabled={isSubmitting || isLoadingDepartments}
                              >
                                 <option value="">— sem departamento —</option>
                                 {departments.map((dept) => (
                                    <option key={dept.id} value={dept.id}>
                                       {dept.department_name}
                                    </option>
                                 ))}
                              </FieldSelect>
                           </Field>
                        )}
                        <Field $span={canAllocate ? 6 : 12}>
                           <FieldLabel>Vendedor responsável</FieldLabel>
                           <ControlScope>
                              <EmployeeNameSearch
                                 value={sellerSearchText}
                                 onChange={setSellerSearchText}
                                 onSelect={handleSellerSelect}
                                 extraParams={SELLER_SEARCH_PARAMS}
                                 initialSelected={null}
                                 placeholder="Buscar funcionário por nome…"
                                 disabled={
                                    isSubmitting ||
                                    formData.is_salesperson_external
                                 }
                              />
                           </ControlScope>
                           <InlineCheckboxLabel>
                              <input
                                 type="checkbox"
                                 checked={formData.is_salesperson_external}
                                 onChange={(e) =>
                                    updateField(
                                       'is_salesperson_external',
                                       e.target.checked,
                                    )
                                 }
                                 disabled={isSubmitting}
                              />
                              Vendedor externo (não cadastrado)
                           </InlineCheckboxLabel>
                        </Field>
                     </FormGrid>
                     {!canAllocate && (
                        <PermNote>
                           <span className="ic">
                              <FaExclamationTriangle size={14} />
                           </span>
                           Departamento fica disponível apenas para
                           operação/gestão.
                        </PermNote>
                     )}

                     {/* Observações */}
                     <Subhead>
                        <span className="sic">
                           <FaRegStickyNote size={14} />
                        </span>
                        <span className="st">Observações</span>
                     </Subhead>
                     <FormGrid>
                        <Field $span={12}>
                           <FieldTextarea
                              value={formData.notes}
                              onChange={(e) =>
                                 updateField('notes', e.target.value)
                              }
                              placeholder="Informações adicionais sobre o agendamento"
                              disabled={isSubmitting}
                           />
                        </Field>
                     </FormGrid>
                  </CollapseBody>
               )}
            </Collapse>

            {/* Rodapé ------------------------------------------------------ */}
            <FormFoot>
               <span className="note">
                  <FaLink size={12} /> Endereço e coordenada vêm do cadastro do
                  cliente.
               </span>
               <div className="acts">
                  <GhostButton
                     type="button"
                     onClick={() => navigate(-1)}
                     disabled={isSubmitting}
                  >
                     Cancelar
                  </GhostButton>
                  <PrimaryButton type="submit" disabled={isSubmitting}>
                     <FaCheck size={15} /> Cadastrar item
                  </PrimaryButton>
               </div>
            </FormFoot>
         </FormCard>

         {/* Modais (portaled) ------------------------------------------- */}
         {activeModal === 'address' && selectedCustomer && (
            <AddressFormModal
               isOpen
               customerId={selectedCustomer.id}
               onClose={() => setActiveModal(null)}
               onSaved={() => {
                  const prevIds = new Set(addresses.map((a) => a.id));
                  reloadAddressesSelectingNew(selectedCustomer.id, prevIds);
               }}
            />
         )}

         {activeModal === 'customer' && (
            <NewCustomerModal
               isOpen
               onClose={() => setActiveModal(null)}
               onCreated={(customer) => {
                  setActiveModal(null);
                  enrichAndSelectCustomer(customer);
               }}
            />
         )}

         <ConfirmItemModal
            isOpen={isConfirmOpen}
            onClose={() => setIsConfirmOpen(false)}
            onConfirm={handleConfirmSubmit}
            isSubmitting={isSubmitting}
            summary={confirmSummary}
         />

         <RestoreItemDialog
            isOpen={isRestoreOpen}
            onClose={() => setIsRestoreOpen(false)}
            item={restorableDuplicate}
            onRestored={(restoredItem) => {
               setIsRestoreOpen(false);
               // O item restaurado É o desfecho da criação — navega pra ficha
               // dele. Também no 422 de corrida (alguém já restaurou): a ficha
               // mostra o estado real.
               const targetId = restoredItem?.id ?? restorableDuplicate?.id;
               if (targetId) navigate(`/routing/items/${targetId}`);
            }}
         />
      </form>
   );
}

ManualEntryTab.propTypes = {
   scheduleDate: PropTypes.string,
   schedulePeriod: PropTypes.oneOf([
      SCHEDULE_PERIOD_MORNING,
      SCHEDULE_PERIOD_AFTERNOON,
   ]),
   onScheduleDateChange: PropTypes.func,
   onSchedulePeriodChange: PropTypes.func,
   clusterOrigin: PropTypes.string,
   clusterOrigins: PropTypes.arrayOf(
      PropTypes.shape({
         origin_code: PropTypes.string,
         origin_label: PropTypes.string,
      }),
   ),
   onClusterOriginChange: PropTypes.func,
   canAllocate: PropTypes.bool,
};
